import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {AuthenticationService} from '@datev/ngx-dcal';
import {AppStateService} from 'commons';
import {filter, map, startWith, switchMap, tap} from 'rxjs';
import {Download, isPackage} from '../../model';
import {PackagesDatasource} from '../../shared/misc/packages-datasource';
import {DownloadStore} from '../../shared/services/download-store';
import {PackageService} from '../../shared/services/package.service';
import { LetDirective, PushPipe } from '@ngrx/component';
import { MatDivider } from '@angular/material/divider';
import { DownloadsTableComponent } from '../../components/downloads-table/downloads-table.component';
import { DacklExpansionPanelComponent, DacklEmptyStateComponent } from '@datev/ngx-dackl';
import { LoggedOutStateComponent } from '../../components/logged-out-state/logged-out-state.component';

@Component({
    selector: 'dp-packages',
    templateUrl: './packages.component.html',
    imports: [
        LetDirective,
        MatDivider,
        DownloadsTableComponent,
        DacklExpansionPanelComponent,
        DacklEmptyStateComponent,
        LoggedOutStateComponent,
        PushPipe,
    ],
})
export class PackagesComponent implements OnInit, OnDestroy {
  public isLoggedIn$ = this.auth.isLoggedIn
    .asObservable()
    .pipe(startWith(false));
  public allDataLoaded: boolean = false;
  public allArchiveDataLoaded: boolean = false;
  private pageNumber: number = 0;
  private archivePageNumber: number = 0;
  private pageSize: number = 20;

  public readonly packageDatasource: PackagesDatasource =
    new PackagesDatasource();

  public readonly archiveDatasource: PackagesDatasource =
    new PackagesDatasource();

  public selectedDownload$ = this.downloadStore.selectedDownload$.pipe(
    map(selectedDownload =>
      selectedDownload !== undefined && isPackage(selectedDownload)
        ? selectedDownload
        : undefined,
    ),
  );

  public mainComponents$ = this.isLoggedIn$.pipe(
    filter(Boolean),
    switchMap(() => this.appState.activeBusinessPartner$),
    map(businessPartner => businessPartner.hknrs?.join(',') || ''),
  );

  constructor(
    private readonly auth: AuthenticationService,
    private readonly packageService: PackageService,
    private readonly appState: AppStateService,
    private readonly downloadStore: DownloadStore,
    @Inject('Window') private readonly window: Window,
  ) {}

  public ngOnInit(): void {
    this.loadPackages();
    this.loadArchivedPackages();
  }

  ngOnDestroy(): void {
    this.packageDatasource.cleanUp();
    this.archiveDatasource.cleanUp();
    this.downloadStore.resetSelectedDownload();
  }

  private loadPackages(): void {
    this.packageDatasource.addDownloads(
      this.mainComponents$.pipe(
        filter(mainComponents => mainComponents !== ''),
        switchMap(mainComponents =>
          this.packageService.getPackagePage(
            { mainComponents: mainComponents, isArchived: false },
            this.pageNumber,
            this.pageSize,
          ),
        ),
        tap(packagePage => {
          this.allDataLoaded = packagePage.last;
        }),
        map(packagePage => packagePage.content),
      ),
    );
  }

  private loadArchivedPackages(): void {
    this.archiveDatasource.addDownloads(
      this.mainComponents$.pipe(
        filter(mainComponents => mainComponents !== ''),
        switchMap(mainComponents =>
          this.packageService.getPackagePage(
            { mainComponents: mainComponents, isArchived: true },
            this.archivePageNumber,
            this.pageSize,
          ),
        ),
        tap(packagePage => {
          this.allArchiveDataLoaded = packagePage.last;
        }),
        map(packagePage => packagePage.content),
      ),
    );
  }

  public loadMorePackages(): void {
    if (!this.allDataLoaded) {
      this.pageNumber++;
      this.loadPackages();
    }
  }

  public loadMoreArchivedPackages(): void {
    if (!this.allArchiveDataLoaded) {
      this.archivePageNumber++;
      this.loadArchivedPackages();
    }
  }

  public handleTableRowSelection(download: Download): void {
    this.downloadStore.setSelectedDownload(download);
  }

  public reloadSite(): void {
    this.window.location.reload();
  }
}
